import { useLazyQuery, useQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Link,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Tabs,
  TextField,
  type Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { differenceInDays, format } from "date-fns";
import debounce from "lodash.debounce";
import { useSnackbar } from "notistack";
import { isEmpty, not } from "ramda";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BulkInviteSelectionModal from "src/components/BulkInvite/BulkInviteWithSelection/BulkInviteSelectionModal";
import PageHeader from "src/components/PageHeader";
import UsersPageTableHead from "src/components/UsersPageTableHead";
import { useTableSelect } from "src/hooks/useTableSelect";
import { makeStyles } from "tss-react/mui";
import AdvancedSearch from "../components/AdvancedSearch";
import UsersPageUserDetails, { type MemberStats } from "../components/UsersPageUserDetails";
import { GET_SELECTED_USERS, MEMBER_STATS, SEARCH_USERS } from "../gql/queries/user";
import { buildUsersPageApiFilters } from "../helpers/adminUsersFilters";
import {
  MEMBERSHIP_LABELS,
  Membership,
  membershipStatuses,
  PAGE_DEFAULT,
  ROWS_PER_PAGE_OPTIONS,
  UrlFilterParams,
} from "../helpers/const";
import { useOnOffSwitch, usePagination, useSwitch } from "../helpers/hooks";
import { transformUsersDataToCSV } from "../helpers/utils";
import { AdminUsersOrderBy, SortDirection, Spacing } from "../types/enum";
import { type UserModel } from "../types/graphql";

/**
 * Styles
 */
const useStyles = makeStyles()((theme: Theme) => ({
  search: {
    width: "50%",
  },
  // The members table scrolls inside its own box, so the scrollbar must be
  // visible by default (macOS hides overlay scrollbars until touched) —
  // otherwise rows below the fold are undiscoverable (hey-lady#4458).
  scrollBox: {
    // +52px vs. the old 380px: the pagination row now lives below the box
    // instead of inside it, so the page keeps fitting the viewport.
    maxHeight: "calc(100vh - 432px)",
    minHeight: 320,
    scrollbarWidth: "thin",
    scrollbarColor: `${theme.palette.divider} transparent`,
    "&::-webkit-scrollbar": { width: 12, height: 12 },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: 6,
    },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
  },
}));

const ACCOUNT_RECOVERY_PERIOD_DAYS = 30;
/** Legacy value of the old "Cancelled members" tab URL param, read for old links. */
const LEGACY_CANCELLED_TAB = "cancelled";
/** Recovery-window sub-filter values; the window is the default, "all" opts out. */
const RECOVERY_WINDOW = "window";
const RECOVERY_ALL = "all";

const MEMBERSHIP_VALUES: string[] = Object.values(Membership);
const isMembership = (value: string | null): value is Membership =>
  value !== null && MEMBERSHIP_VALUES.includes(value);
const MEMBERSHIP_TAB_ORDER: Membership[] = [Membership.Current, Membership.Past, Membership.All];

const UsersPage: React.FC = () => {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const filtersStr = location.search;
  const queryParams = new URLSearchParams(filtersStr);

  // Which members the page is about. One tab strip answers this — there is no
  // second population control, so contradictions like "Current members tab +
  // Past members toggle" can no longer be expressed. Defaults to Current:
  // most people in the database have already left, and showing them by
  // default buries everyone still worth contacting.
  const view = (() => {
    const fromUrl = queryParams.get(UrlFilterParams.View);
    if (isMembership(fromUrl)) {
      return fromUrl;
    }
    // Legacy links: the old membership toggle param, then the old tab strip.
    const legacyMembership = queryParams.get(UrlFilterParams.Membership);
    if (isMembership(legacyMembership)) {
      return legacyMembership;
    }
    if (queryParams.get("tab") === LEGACY_CANCELLED_TAB) {
      return Membership.Past;
    }
    return Membership.Current;
  })();

  // The old "Cancelled members" tab lives on as the recovery window inside
  // the Recovery Period Members view, which opens by default; `recovery=all`
  // is the opt-out.
  const cancelledInRecovery =
    view === Membership.Past && queryParams.get(UrlFilterParams.Recovery) !== RECOVERY_ALL;
  const recoveryMode = cancelledInRecovery ? RECOVERY_WINDOW : RECOVERY_ALL;

  const readParam = (key: string) => {
    const values = queryParams.get(key);
    if (!values) {
      return [];
    }
    return values.split(",");
  };

  const filters = {
    subscriptionStatus: readParam(UrlFilterParams.Subscription),
    plan: readParam(UrlFilterParams.Plan),
    kind: readParam(UrlFilterParams.Kind),
    location: JSON.parse(queryParams.get(UrlFilterParams.Location) ?? "[]"),
    interests: readParam(UrlFilterParams.Interests),
    englishSkills: readParam(UrlFilterParams.Skills),
    englishLevels: readParam(UrlFilterParams.Levels),
    openToSpeakingPartners: readParam(UrlFilterParams.Open),
    occupations: readParam(UrlFilterParams.Occupations),
    industries: readParam(UrlFilterParams.Industries),
    badges: readParam(UrlFilterParams.Badges),
    verificationStatus: readParam(UrlFilterParams.VerificationStatus),
    zones: readParam(UrlFilterParams.Zones),
    tenures: readParam(UrlFilterParams.Tenures),
    minEventsAttended: queryParams.get(UrlFilterParams.MinEventsAttended) ?? "",
    storyKeyword: queryParams.get(UrlFilterParams.StoryKeyword) ?? "",
    cancelledInRecovery,
  };

  const [query, setQuery] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [expanded, expand] = useSwitch();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = usePagination<UserModel>();
  const [order, setOrder] = useState<SortDirection>(
    cancelledInRecovery ? SortDirection.ASC : SortDirection.DESC,
  );
  const [orderBy, setOrderBy] = useState<AdminUsersOrderBy>(
    cancelledInRecovery ? AdminUsersOrderBy.RecoveryDays : AdminUsersOrderBy.Date,
  );

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: AdminUsersOrderBy) => {
    const isAsc = orderBy === property && order === SortDirection.ASC;
    setOrder(isAsc ? SortDirection.DESC : SortDirection.ASC);
    setOrderBy(property);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: we wannt to change page on filtersStr change
  useEffect(() => {
    onChangePage(null, PAGE_DEFAULT);
  }, [filtersStr, onChangePage]);

  useEffect(() => {
    setOrder(cancelledInRecovery ? SortDirection.ASC : SortDirection.DESC);
    setOrderBy(cancelledInRecovery ? AdminUsersOrderBy.RecoveryDays : AdminUsersOrderBy.Date);
  }, [cancelledInRecovery]);

  // Picking statuses by hand in the filters panel is more specific than the
  // Current/Recovery Period/Everyone tabs, so it wins — until the population changes.
  const subscriptionStatus = not(isEmpty(filters.subscriptionStatus))
    ? filters.subscriptionStatus
    : membershipStatuses(view);

  // Everything the page sends to the API starts from one prepared object: the
  // URL filters converted to the API's types, plus the search box and the
  // population scope. The table, the wider-search probe, CSV export, copy
  // emails and the bulk invite all share it, so an invite list can never be a
  // different population than the one on screen.
  const apiFilters = buildUsersPageApiFilters(filters, { query: searchValue, subscriptionStatus });

  const { loading, data } = useQuery(SEARCH_USERS, {
    fetchPolicy: "network-only",
    variables: {
      filters: {
        ...apiFilters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        order: order.toUpperCase(),
        orderBy,
      },
    },
  });

  // When a search finds nobody in the current population, check whether the
  // same search would find someone among everyone. That mismatch is exactly
  // the confusion the merged tabs fix, so offer the one-click way out.
  const widerSearchNeeded =
    view !== Membership.All &&
    not(isEmpty(searchValue)) &&
    not(loading) &&
    (data?.adminListUsers?.total ?? 0) === 0;
  const [runWiderSearch, { data: widerData }] = useLazyQuery(SEARCH_USERS, {
    fetchPolicy: "network-only",
  });
  const widerTotal: number | undefined = widerData?.adminListUsers?.total;
  // biome-ignore lint/correctness/useExhaustiveDependencies: refire only when the need or the term changes
  useEffect(() => {
    if (!widerSearchNeeded) {
      return;
    }
    void runWiderSearch({
      variables: {
        filters: {
          ...apiFilters,
          limit: 1,
          offset: 0,
          // Same advanced filters, but without the population scope.
          subscriptionStatus: [],
          cancelledInRecovery: false,
        },
      },
    });
  }, [widerSearchNeeded, searchValue]);

  const [runQueryToExport, { loading: exportLoading, error: exportError }] = useLazyQuery(
    GET_SELECTED_USERS,
    {
      onCompleted: (data) => {
        const csvData = transformUsersDataToCSV(data?.adminGetSelectedUsers);
        const csvBlob = new Blob([csvData], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(csvBlob);

        const link = document.createElement("a");
        link.href = downloadUrl;

        const formattedDate = format(new Date(), "yyyy-MM-dd");
        const filename = `HL-Export_${formattedDate}_(${data?.adminGetSelectedUsers.length}members).csv`;

        link.download = filename;
        link.click();
      },
    },
  );

  const [emailsCopied, setEmailsCopied] = useState(false);

  // Reuses the export query on purpose: it already returns everyone the filters
  // match rather than the page on screen, which is the whole point of copying.
  const [runQueryToCopyEmails, { loading: copyLoading }] = useLazyQuery(GET_SELECTED_USERS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const emails = (data?.adminGetSelectedUsers ?? [])
        .map((user: UserModel) => user.email)
        .filter(Boolean);

      if (isEmpty(emails)) {
        enqueueSnackbar("No members to copy.", { variant: "info" });
        return;
      }

      navigator.clipboard.writeText(emails.join(", ")).then(
        () => {
          setEmailsCopied(true);
          setTimeout(() => setEmailsCopied(false), 1500);
          enqueueSnackbar(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}.`, {
            variant: "success",
          });
        },
        () => {
          enqueueSnackbar("Could not write to the clipboard.", { variant: "error" });
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  /**
   * Nothing ticked means everyone the filters describe — the common case, and
   * how the member tools' explorer copies a group. Ticking rows narrows it.
   *
   * Passes the search box and membership switch too, which the CSV export
   * leaves out, so what lands on the clipboard is what's on screen.
   */
  const copyEmails = () => {
    void runQueryToCopyEmails({
      variables: {
        filters: {
          ...apiFilters,
          ids: selected,
          all: allSelected || isEmpty(selected),
        },
      },
    });
  };

  const exportUsers = () => {
    if (isEmpty(selected) && not(allSelected)) {
      enqueueSnackbar("Select users first.", { variant: "info" });
      return;
    }
    void runQueryToExport({
      variables: {
        filters: {
          ...apiFilters,
          ids: selected,
          all: allSelected,
        },
      },
    });
  };

  const debounceCallback = useCallback(
    debounce((searchVal: string) => {
      setSearchValue(searchVal);
    }, 1000),
    [],
  );

  const handleChange = (event: any) => {
    setQuery(event.target.value);

    debounceCallback(event.target.value);
  };

  if (exportError) {
    enqueueSnackbar(exportError.message, { variant: "error" });
  }

  const items = data?.adminListUsers?.items || [];
  const pageCount = Math.ceil(data?.adminListUsers?.total / rowsPerPage);
  const validPage = page < pageCount ? page : PAGE_DEFAULT;

  // Zone and attendance have to be worked out per member, so they're fetched
  // for the whole page in one go once the rows are known.
  const pageUserIds = items.map((user: UserModel) => user.id);
  const { data: statsData } = useQuery(MEMBER_STATS, {
    variables: { userIds: pageUserIds },
    skip: pageUserIds.length === 0,
  });

  const statsByUser = new Map<string, MemberStats>(
    (statsData?.adminMemberStats ?? []).map((stat: MemberStats & { userId: string }) => [
      stat.userId,
      stat,
    ]),
  );

  const { selected, setSelected, allSelected, onSelectOne, onSelectedAll, clearSelection } =
    useTableSelect();

  const idsOnCurrentPage = items.map((item: UserModel) => item.id);
  const allRowsChecked =
    selected.length > 0 && idsOnCurrentPage.every((id: string) => selected.includes(id));

  const selectedOnCurrentPage = selected.filter((id: string) =>
    idsOnCurrentPage.includes(id),
  ).length;
  const [selectCurrentPageChecked, setSelectCurrentPageChecked] = useState(
    allRowsChecked || allSelected,
  );

  const shouldClearSelection =
    selectedOnCurrentPage > 1 && !allSelected && !selectCurrentPageChecked;

  const onSelectCurrentPage = () => {
    if (selectedOnCurrentPage > 1 && !allSelected && !selectCurrentPageChecked) {
      const newSelected = selected.filter((id) => !idsOnCurrentPage.includes(id));
      setSelected(newSelected);
      return;
    }

    if (!selectCurrentPageChecked) {
      const idsOnCurrentPage = items.map((item: UserModel) => item.id);
      const newSelected = Array.from(new Set([...selected, ...idsOnCurrentPage]));
      setSelected(newSelected);
    } else {
      const idsToRemove = new Set(items.map((item: UserModel) => item.id));
      const newSelected = selected.filter((id) => !idsToRemove.has(id));
      setSelected(newSelected);
    }
    setSelectCurrentPageChecked((prev: boolean) => !prev);
  };

  useEffect(() => {
    setSelectCurrentPageChecked(allRowsChecked || allSelected);
  }, [allRowsChecked, allSelected]);

  const [openInviteModal, onOpenInviteModal, onCloseInviteModal] = useOnOffSwitch();

  const navigateToView = (next: Membership) => {
    const params = new URLSearchParams(filtersStr);
    params.set(UrlFilterParams.View, next);
    // The hand-picked statuses would override the new population, so clear them.
    params.delete(UrlFilterParams.Subscription);
    // The legacy tab/membership params are superseded by `view`; drop them so
    // links shared after any click are canonical.
    params.delete("tab");
    params.delete(UrlFilterParams.Membership);
    params.delete(UrlFilterParams.Recovery);
    void navigate({
      pathname: location.pathname,
      search: params.toString(),
    });
    clearSelection();
  };

  const handleViewChange = (_event: React.SyntheticEvent, value: string) => {
    navigateToView(value as Membership);
  };

  const handleRecoveryChange = (_event: React.MouseEvent<HTMLElement>, value: string) => {
    if (!value || value === recoveryMode) {
      return;
    }
    const params = new URLSearchParams(filtersStr);
    if (value === RECOVERY_ALL) {
      params.set(UrlFilterParams.Recovery, RECOVERY_ALL);
    } else {
      // The recovery window is the default; its absence encodes it.
      params.delete(UrlFilterParams.Recovery);
    }
    void navigate({ search: params.toString() });
    clearSelection();
  };

  const getRecoveryDaysRemaining = (recoveryStartedAt?: string | null): number | undefined => {
    if (!recoveryStartedAt) {
      return undefined;
    }
    return Math.max(
      0,
      ACCOUNT_RECOVERY_PERIOD_DAYS - differenceInDays(new Date(), new Date(recoveryStartedAt)),
    );
  };

  const handleInviteClick = () => {
    if (isEmpty(selected) && not(allSelected)) {
      enqueueSnackbar("Copy Event Id and Select participants.", { variant: "info" });
      return;
    }
    onOpenInviteModal();
  };

  return (
    <>
      <PageHeader title="Members">
        Everyone who has ever joined, searchable by who they are and how they&apos;re doing. Opens
        on current members — most of the database has already left — and the filters carry into the
        invite and export actions below.
      </PageHeader>

      <Box mb={Spacing.m} display="flex" alignItems="center" gap={Spacing.m} flexWrap="wrap">
        <Typography variant="body2" color="textSecondary">
          {loading
            ? "Counting…"
            : `${data?.adminListUsers?.total ?? 0} member${data?.adminListUsers?.total === 1 ? "" : "s"}`}
          {not(isEmpty(filters.subscriptionStatus))
            ? " · status picked by hand in Advanced Search"
            : ""}
        </Typography>
      </Box>

      <Box mb={Spacing.m}>
        <Tabs value={view} onChange={handleViewChange}>
          {MEMBERSHIP_TAB_ORDER.map((option) => (
            <Tab key={option} value={option} label={MEMBERSHIP_LABELS.get(option)} />
          ))}
        </Tabs>
      </Box>

      {view === Membership.Past ? (
        <Box mb={Spacing.m} display="flex" alignItems="center" gap={Spacing.m}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={recoveryMode}
            onChange={handleRecoveryChange}
          >
            <ToggleButton value={RECOVERY_WINDOW}>
              {`In recovery window (${ACCOUNT_RECOVERY_PERIOD_DAYS}d)`}
            </ToggleButton>
            <ToggleButton value={RECOVERY_ALL}>All recovery period members</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="textSecondary">
            Cancelled members who can still undo it
          </Typography>
        </Box>
      ) : null}

      <Box mb={Spacing.m} display="flex" alignItems="center">
        <Box className={classes.search} width="50%" alignItems="center">
          <TextField
            fullWidth
            label={`Search ${MEMBERSHIP_LABELS.get(view)?.toLowerCase() ?? "users"}`}
            variant="outlined"
            value={query}
            onChange={handleChange}
          />
        </Box>
        <Box display="flex" alignItems="center" width="50%" justifyContent="space-between">
          <Box ml={Spacing.l} display="flex" alignItems="center">
            <Typography variant="h4" color="textPrimary">
              Advanced Search
            </Typography>
            <IconButton onClick={expand}>
              {expanded ? <ExpandLessIcon fontSize="large" /> : <ExpandMoreIcon fontSize="large" />}
            </IconButton>
          </Box>
          {!cancelledInRecovery ? (
            <Box mx={Spacing.sm}>
              <Button variant="outlined" onClick={handleInviteClick}>
                Invite to Event
              </Button>
            </Box>
          ) : null}

          <Box mr={Spacing.sm}>
            <Button variant="outlined" onClick={copyEmails} disabled={copyLoading}>
              {emailsCopied ? "✓ Copied" : "Copy Emails"}
            </Button>
          </Box>

          <Button variant="outlined" onClick={exportUsers} disabled={exportLoading}>
            Export Selection
          </Button>
        </Box>
      </Box>

      {expanded ? (
        <Box mb={Spacing.l}>
          <AdvancedSearch
            filters={filters}
            loading={loading}
            expanded={expanded}
            clearSelection={clearSelection}
          />
        </Box>
      ) : null}

      {widerSearchNeeded && (widerTotal ?? 0) > 0 ? (
        <Box mb={Spacing.m}>
          <Typography variant="body2" color="textSecondary">
            {`No ${MEMBERSHIP_LABELS.get(view)?.toLowerCase()} match this search — `}
            <Link component="button" variant="body2" onClick={() => navigateToView(Membership.All)}>
              show everyone instead
            </Link>
          </Typography>
        </Box>
      ) : null}

      <Box display="flex" justifyContent="space-between" mb={Spacing.s}>
        <Box display="flex" alignItems="center">
          {allRowsChecked && !allSelected ? (
            <Box mr={Spacing.m}>
              <Typography variant="h5" color="textPrimary">
                {`All ${data?.adminListUsers.items.length} users on this page are selected`}
              </Typography>
            </Box>
          ) : null}
          {allSelected ? (
            <Button style={{ textTransform: "none" }} onClick={clearSelection}>
              <Typography variant="h5" color="secondary">
                Clear selection of all users
              </Typography>
            </Button>
          ) : (
            <Button style={{ textTransform: "none" }} onClick={onSelectedAll}>
              <Typography variant="h5" color="secondary">
                Select all {data?.adminListUsers.total || 0} in this list
              </Typography>
            </Button>
          )}
        </Box>
        <Typography variant="h5" color="textPrimary">
          {`${allSelected ? data?.adminListUsers.total : selected.length} users selected`}
        </Typography>
      </Box>

      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" p={Spacing.xxl}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer className={classes.scrollBox}>
              <Table stickyHeader>
                <UsersPageTableHead
                  order={order}
                  orderBy={orderBy}
                  selectCurrentPageChecked={selectCurrentPageChecked}
                  shouldClearSelection={shouldClearSelection}
                  onRequestSort={handleRequestSort}
                  onSelectCurrentPage={onSelectCurrentPage}
                  showRecoveryDays={cancelledInRecovery}
                />
                <TableBody>
                  {items.map((user: UserModel) => (
                    <UsersPageUserDetails
                      key={user.id}
                      user={user}
                      selected={selected}
                      onSelectOne={onSelectOne}
                      allSelected={allSelected}
                      stats={statsByUser.get(user.id)}
                      storyKeyword={filters.storyKeyword}
                      recoveryDaysRemaining={
                        cancelledInRecovery
                          ? getRecoveryDaysRemaining(user.recoveryStartedAt)
                          : undefined
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pinned below the scroll box so the pager is always on screen: it
              used to sit at the bottom of the box's scroll content, out of
              sight under the rows (hey-lady#4458). */}
            <TablePagination
              component="div"
              sx={{ borderTop: 1, borderColor: "divider" }}
              page={validPage}
              count={data?.adminListUsers.total || 0}
              onPageChange={onChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onChangeRowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        )}
      </Card>
      {
        <BulkInviteSelectionModal
          open={openInviteModal}
          onClose={onCloseInviteModal}
          apiFilters={apiFilters}
          selected={selected}
          allSelected={allSelected}
        />
      }
    </>
  );
};

export default UsersPage;
